import styled from "styled-components";

// ===== INTERFACE DO TEMA =====
interface Theme {
  colors: {
    background: string;
    text: string;
    selection: string;
    modalColor: string;
    border: string;
    primary?: string;
    danger?: string;
    selectionDark?: string;
    textOnPrimary?: string;
    label?: string;
    borderColor?: string;
  };
}

// ===== COMPONENTES DE FORMULÁRIO =====
export const Input = styled.input`
  width: 100%;
  padding: var(--sp-12);
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 6px;
  font-size: var(--fs-md);
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.selection};
    box-shadow: 0 0 0 2px ${(props) => props.theme.colors.selection + "40"};
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.label};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// ===== COMPONENTES DE BOTÃO =====
export const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-12);
  margin-top: var(--sp-24);
  flex-wrap: wrap;
`;

export const Button = styled.button<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-12) var(--sp-24);
  border-radius: 8px;
  border: none;
  background-color: ${(props) => props.color || props.theme.colors.selection};
  color: ${(props) => props.theme.colors.textOnPrimary};
  font-weight: 600;
  font-size: var(--fs-md);
  cursor: pointer;
  transition: background-color 0.3s, transform 0.2s, opacity 0.2s;
  gap: var(--sp-8);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    opacity: 0.9;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// ===== COMPONENTES DE ÍCONES =====
export const MaterialIcons = styled.span`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: var(--fs-xl);
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

// ===== O QUE A ABA REESCRITA PRECISA =====
//
// Mesmos tokens e mesmo desenho da aba Meios de Pagamento, que é o molde certo
// deste repositório: cabeçalho que explica o negócio, tabela enxuta, aviso
// tonalizado. Nada de lib de UI nem de tema novo.

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-16);
  padding: var(--sp-16) var(--sp-20) var(--sp-24);
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  padding-bottom: var(--sp-12);
  border-bottom: 1px solid
    ${(props) => props.theme.colors.borderColor || "#e0e0e0"};
`;

export const HeaderTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: var(--sp-10);
  margin: 0;
  font-size: var(--fs-2xl);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

export const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
`;

export const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-md);

  th,
  td {
    text-align: left;
    padding: var(--sp-10) var(--sp-12);
    border-bottom: 1px solid
      ${(props) => props.theme.colors.borderColor || "#e0e0e0"};
  }

  th {
    font-size: var(--fs-xs);
    letter-spacing: 0.05em;
    text-transform: uppercase;
    font-weight: 600;
    color: ${(props) => props.theme.colors.label};
  }
`;

export const Selo = styled.span<{ $ativo: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: var(--sp-2) var(--sp-10);
  border-radius: 999px;
  font-size: var(--fs-xs);
  font-weight: 600;
  border: 1px solid ${(props) => (props.$ativo ? "#b7e0bd" : "#e0d6b7")};
  background: ${(props) => (props.$ativo ? "#eef8ef" : "#faf6ea")};
  color: ${(props) => (props.$ativo ? "#1e5c28" : "#7a5a12")};
`;

export const Paginacao = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-12);
  font-size: var(--fs-sm);
  color: ${(props) => props.theme.colors.label};
`;

/** Mesmos três tons do resto da Configuração: ok, atenção, erro. */
export const Aviso = styled.div<{ $tom: "ok" | "erro" | "atencao" }>`
  padding: var(--sp-12);
  border-radius: 8px;
  font-size: var(--fs-sm);
  line-height: 1.55;
  border: 1px solid
    ${(props) =>
      props.$tom === "ok"
        ? "#b7e0bd"
        : props.$tom === "erro"
        ? "#f0b7b2"
        : "#f0dcae"};
  background: ${(props) =>
    props.$tom === "ok"
      ? "#eef8ef"
      : props.$tom === "erro"
      ? "#fdf0ef"
      : "#fdf7e7"};
  color: ${(props) =>
    props.$tom === "ok"
      ? "#1e5c28"
      : props.$tom === "erro"
      ? "#8c2b23"
      : "#7a5a12"};
`;

export const Vazio = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-8);
  padding: var(--sp-40);
  text-align: center;
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
`;

// Exportação padrão para compatibilidade
export default {
  Input,
  ButtonWrapper,
  Button,
  MaterialIcons,
  Container,
  Header,
  HeaderTitle,
  HeaderSubtitle,
  Tabela,
  Selo,
  Paginacao,
  Aviso,
  Vazio,
};
